import React, { useState, useRef, useEffect } from 'react';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import useManageWeeklyShip from './useManageWeeklyShip';
import useShipDate from '../setting/shipDate/useShipDate';
import { toast } from 'react-toastify';

const WeeklyShipIshipImport = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('matched');
  const fileInputRef = useRef();

  const { previewIshipImport, applyIshipImport, getShipmentGroupSummaryByShipDateId } = useManageWeeklyShip();
  const { getShipDates } = useShipDate();

  useEffect(() => {
    getShipDates();
  }, []);

  const handleShipDateChange = async (e) => {
    const id = e.target.value;
    setShipDateId(id);
    setPreviewData(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (!id || id === 'default') {
      setAllGroups([]);
      return;
    }

    try {
      const response = await getShipmentGroupSummaryByShipDateId(id);
      const data = response?.data ? response.data : (Array.isArray(response) ? response : []);
      setAllGroups(data);
    } catch (err) {
      console.error(err);
      toast.error('โหลดสรุปกลุ่มจัดส่งล้มเหลว');
      setAllGroups([]);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setPreviewData(null);
  };

  const handlePreview = async () => {
    if (!shipDateId || shipDateId === 'default') {
      toast.warning('กรุณาเลือกวันที่จัดส่งก่อน');
      return;
    }
    if (!file) {
      toast.warning('กรุณาเลือกไฟล์ Excel ก่อน');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('shipDateId', shipDateId);

      const result = await previewIshipImport(formData);
      if (result) {
        setPreviewData(result);
        setActiveTab('matched');
        toast.success(`พบ ${result.matched?.length || 0} รายการที่ตรงกัน`);
      } else {
        toast.error('ไม่สามารถประมวลผลไฟล์ได้');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'เกิดข้อผิดพลาดในการอ่านไฟล์');
    } finally {
      setIsUploading(false);
    }
  };

  const handleApply = async () => {
    if (!previewData?.matched || previewData.matched.length === 0) {
      toast.warning('ไม่มีรายการที่ตรงกันเพื่อนำเข้า');
      return;
    }

    setIsApplying(true);
    try {
      const matchedPayload = previewData.matched.map(m => ({
        shipmentGroupId: m.shipmentGroup.id,
        cartonId: m.carton?.id || null, // matched by reference → that ลัง (step 11)
        tracking: m.iship.tracking,
        thaiDeliveryFee: m.iship.thaiDeliveryFee,
      }));

      const result = await applyIshipImport({
        shipDateId,
        matchedData: matchedPayload,
      });

      if (result?.success) {
        const appliedCount = result.results?.filter(r => r.status === 'applied').length || 0;
        toast.success(`นำเข้าสำเร็จ ${appliedCount} รายการ`);
        setPreviewData(null);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.error('ไม่สามารถนำเข้าข้อมูลได้');
      }
    } catch (err) {
      console.error(err);
      toast.error('เกิดข้อผิดพลาดในการนำเข้า');
    } finally {
      setIsApplying(false);
    }
  };

  const deliveryGroupCount = allGroups.filter(g => g.shipmentGroup?.receiveMethod === 'delivery').length;
  const pickupGroupCount = allGroups.filter(g => g.shipmentGroup?.receiveMethod === 'pickup').length;
  const lalamoveGroupCount = allGroups.filter(g => g.shipmentGroup?.receiveMethod === 'lalamove').length;

  return (
    <div className="min-h-screen p-2 sm:p-4 md:p-6 flex flex-col gap-4">
      {/* Header Controls */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="w-full sm:max-w-md">
          <ShipDateDropdown
            handleDataChange={handleShipDateChange}
            includeDefault={true}
            airOnly={true}
          />
        </div>
        <div className="w-full flex flex-col sm:flex-row sm:items-end gap-2 min-w-0">
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">ไฟล์ iShip (Excel)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <button
            onClick={handlePreview}
            disabled={!shipDateId || !file || isUploading}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              !shipDateId || !file || isUploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isUploading ? 'กำลังประมวลผล...' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Shipment Group Overview */}
      {allGroups.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-base font-semibold mb-2">สรุปกลุ่มจัดส่ง ({allGroups.length} กลุ่ม)</h3>
          <div className="flex gap-4 text-sm mb-3">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
              Delivery: {deliveryGroupCount}
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
              Pickup: {pickupGroupCount}
            </span>
            <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full">
              Lalamove: {lalamoveGroupCount}
            </span>
          </div>
          <div className="max-h-48 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">ลูกค้า</th>
                  <th className="text-left px-3 py-2">Smile Name</th>
                  <th className="text-left px-3 py-2">ชื่อที่อยู่</th>
                  <th className="text-left px-3 py-2">วิธีรับ</th>
                  <th className="text-left px-3 py-2">ขนส่ง</th>
                  <th className="text-left px-3 py-2">กล่อง</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allGroups.map((g, idx) => (
                  <tr key={idx} className={
                    previewData?.unmatchedGroups?.some(ug => ug.shipmentGroup.id === g.shipmentGroup?.id)
                      ? 'bg-red-50'
                      : previewData?.matched?.some(m => m.shipmentGroup.id === g.shipmentGroup?.id)
                        ? 'bg-green-50'
                        : ''
                  }>
                    <td className="px-3 py-1.5">{g.customer?.contact || '-'}</td>
                    <td className="px-3 py-1.5">{g.customer?.smileName || '-'}</td>
                    <td className="px-3 py-1.5">{g.address?.name || '-'}</td>
                    <td className="px-3 py-1.5">{g.shipmentGroup?.receiveMethod || '-'}</td>
                    <td className="px-3 py-1.5">{g.shipmentGroup?.courierTh || '-'}</td>
                    <td className="px-3 py-1.5">{g.boxNos?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewData && (
            <div className="flex gap-3 mt-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-200 rounded inline-block" /> จับคู่แล้ว
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-200 rounded inline-block" /> ยังไม่ได้จับคู่ (delivery)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Preview Results */}
      {previewData && (
        <div className="flex-1 flex flex-col gap-4 min-h-0">
          {/* Tabs */}
          <div className="flex gap-1 border-b">
            <button
              onClick={() => setActiveTab('matched')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === 'matched'
                  ? 'bg-green-100 text-green-800 border-b-2 border-green-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              จับคู่แล้ว ({previewData.matched?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('unmatchedIship')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === 'unmatchedIship'
                  ? 'bg-yellow-100 text-yellow-800 border-b-2 border-yellow-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              iShip ไม่ตรง ({previewData.unmatchedIship?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('unmatchedGroups')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === 'unmatchedGroups'
                  ? 'bg-red-100 text-red-800 border-b-2 border-red-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              กลุ่มไม่ตรง ({previewData.unmatchedGroups?.length || 0})
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto bg-white rounded-lg shadow">
            {activeTab === 'matched' && (
              <table className="w-full text-sm">
                <thead className="bg-green-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">ชื่อ iShip</th>
                    <th className="text-left px-3 py-2">Tracking</th>
                    <th className="text-right px-3 py-2">ค่าส่ง</th>
                    <th className="text-right px-3 py-2">ค่ารีแพ็ค</th>
                    <th className="text-right px-3 py-2">รวม (thaiDeliveryFee)</th>
                    <th className="text-left px-3 py-2">จับคู่กับ</th>
                    <th className="text-left px-3 py-2">ลัง</th>
                    <th className="text-left px-3 py-2">ชื่อที่อยู่</th>
                    <th className="text-left px-3 py-2">กล่อง</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(previewData.matched || []).map((m, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-1.5">{idx + 1}</td>
                      <td className="px-3 py-1.5 font-medium">{m.iship?.name || '-'}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{m.iship?.tracking || '-'}</td>
                      <td className="px-3 py-1.5 text-right">{m.iship?.deliveryFee?.toFixed(2) || '0.00'}</td>
                      <td className="px-3 py-1.5 text-right">{m.iship?.repackingFee?.toFixed(2) || '0.00'}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">
                        {m.iship?.feePending
                          ? <span className="text-orange-600" title="iShip ยังไม่มีราคา — นำเข้าเลขพัสดุอย่างเดียว ค่าส่งไม่เปลี่ยน">รอค่าส่ง</span>
                          : (m.iship?.thaiDeliveryFee?.toFixed(2) || '0.00')}
                      </td>
                      <td className="px-3 py-1.5">{m.customer?.contact || '-'}</td>
                      <td className="px-3 py-1.5">{m.carton ? `ลัง ${m.carton.label}` : <span className="text-orange-600">ชื่อ (ไม่มีอ้างอิง)</span>}</td>
                      <td className="px-3 py-1.5">{m.address?.name || '-'}</td>
                      <td className="px-3 py-1.5">{m.boxNos?.map(b => b.boxNo).join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'unmatchedIship' && (
              <div>
                {(previewData.unmatchedIship || []).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">ทุกรายการ iShip จับคู่ได้แล้ว</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-yellow-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">ชื่อ iShip</th>
                        <th className="text-left px-3 py-2">Tracking</th>
                        <th className="text-right px-3 py-2">ค่าส่ง</th>
                        <th className="text-right px-3 py-2">ค่ารีแพ็ค</th>
                        <th className="text-right px-3 py-2">รวม</th>
                        <th className="text-left px-3 py-2">สาเหตุ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewData.unmatchedIship.map((row, idx) => (
                        <tr key={idx} className="bg-yellow-50/50">
                          <td className="px-3 py-1.5">{idx + 1}</td>
                          <td className="px-3 py-1.5 font-medium text-yellow-700">{row.name || '-'}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{row.tracking || '-'}</td>
                          <td className="px-3 py-1.5 text-right">{row.deliveryFee?.toFixed(2) || '0.00'}</td>
                          <td className="px-3 py-1.5 text-right">{row.repackingFee?.toFixed(2) || '0.00'}</td>
                          <td className="px-3 py-1.5 text-right">{row.thaiDeliveryFee?.toFixed(2) || '0.00'}</td>
                          <td className="px-3 py-1.5 text-xs text-gray-600">{row.reason || 'ไม่พบชื่อที่ตรงกัน'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'unmatchedGroups' && (
              <div>
                {(previewData.unmatchedGroups || []).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">ทุกกลุ่ม delivery จับคู่ได้แล้ว</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">ลูกค้า</th>
                        <th className="text-left px-3 py-2">Smile Name</th>
                        <th className="text-left px-3 py-2">ชื่อที่อยู่</th>
                        <th className="text-left px-3 py-2">ขนส่ง</th>
                        <th className="text-left px-3 py-2">กล่อง</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewData.unmatchedGroups.map((g, idx) => (
                        <tr key={idx} className="bg-red-50/50">
                          <td className="px-3 py-1.5">{idx + 1}</td>
                          <td className="px-3 py-1.5 font-medium text-red-700">{g.customer?.contact || '-'}</td>
                          <td className="px-3 py-1.5">{g.customer?.smileName || '-'}</td>
                          <td className="px-3 py-1.5">{g.address?.name || '-'}</td>
                          <td className="px-3 py-1.5">{g.shipmentGroup?.courierTh || '-'}</td>
                          <td className="px-3 py-1.5">{g.boxNos?.map(b => b.boxNo).join(', ') || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Apply Button */}
          {previewData.matched?.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={handleApply}
                disabled={isApplying}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold ${
                  isApplying
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isApplying
                  ? 'กำลังนำเข้า...'
                  : `นำเข้า ${previewData.matched.length} รายการ`
                }
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!previewData && allGroups.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          เลือกวันที่จัดส่งและอัปโหลดไฟล์ iShip เพื่อเริ่มต้น
        </div>
      )}
    </div>
  );
};

export default WeeklyShipIshipImport;
